/**
 * Data Mapping Visualization Component
 *
 * Displays interactive visualization of how source data files map to Neo4j schema.
 * Uses vis-network for graph visualization with hierarchical left-to-right layout.
 */

import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Network } from 'vis-network';
import { mappingVisualizationAPI } from '../../lib/api';
import {
  FilterOptions,
  MappingVisualizationData,
} from '../../types/mapping';
import {
  transformToVisNetwork,
  getMappingStats,
  allMappingsAccepted,
} from '../../lib/mappingTransformers';
import { transformToSankey } from '../../lib/sankeyTransformers';
import VisNetworkCanvas from './VisNetworkCanvas';
import SankeyCanvas from './SankeyCanvas';
import Spinner from '../ui/Spinner';
import Button from '../ui/Button';
import Card from '../ui/Card';
import { AlertCircle, Check, FileText, GitBranch, Code, Maximize2, Minimize2, Filter, ZoomIn, ZoomOut, ArrowLeft } from 'lucide-react';

interface MappingVisualizationProps {
  caseId: string;
  onBack?: () => void;
}

type ColumnFilterMode = 'all' | 'mapped' | 'unmapped';

const MappingVisualization: React.FC<MappingVisualizationProps> = ({  caseId, onBack }) => {
  // State
  const [selectedNode, setSelectedNode] = useState<string | null>(null);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [columnFilterMode, setColumnFilterMode] = useState<ColumnFilterMode>('all');
  const [network, setNetwork] = useState<Network | null>(null);
  const [sankeyZoomAPI, setSankeyZoomAPI] = useState<{ zoomIn: () => void; zoomOut: () => void; resetZoom: () => void } | null>(null);
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({
    selectedFiles: [],
    minConfidence: 0,
    showUnmapped: true,
    showAcceptedOnly: false,
    showSuggestedOnly: false,
    showRelationships: true,
    searchTerm: '',
  });

  // Update filter options when column filter mode changes
  const effectiveFilterOptions = useMemo(() => {
    const updated = { ...filterOptions };

    switch (columnFilterMode) {
      case 'mapped':
        // Show only mapped columns (hide unmapped)
        updated.showUnmapped = false;
        updated.showSuggestedOnly = true; // This enables showing only columns with mappings
        updated.showAcceptedOnly = false;
        break;
      case 'unmapped':
        // Show only unmapped columns
        // We'll need to add a special flag for this in the filter
        updated.showUnmapped = true;
        updated.showSuggestedOnly = false;
        updated.showAcceptedOnly = false;
        updated.showOnlyUnmapped = true; // Custom flag
        break;
      case 'all':
      default:
        // Show everything
        updated.showUnmapped = true;
        updated.showAcceptedOnly = false;
        updated.showSuggestedOnly = false;
        updated.showOnlyUnmapped = false;
        break;
    }

    return updated;
  }, [filterOptions, columnFilterMode]);

  // Fetch mapping data
  const {
    data: mappingData,
    isLoading,
    error,
    refetch,
  } = useQuery<MappingVisualizationData>({
    queryKey: ['mapping-visualization', caseId],
    queryFn: () => mappingVisualizationAPI.getMappingData(caseId),
    staleTime: 60000, // 1 minute
  });

  // Transform data for visualizations
  const { nodes, edges } = useMemo(
    () => transformToVisNetwork(mappingData || null, effectiveFilterOptions),
    [mappingData, effectiveFilterOptions]
  );

  // Transform data for Sankey diagram
  const sankeyData = useMemo(
    () => transformToSankey(mappingData || null, effectiveFilterOptions),
    [mappingData, effectiveFilterOptions]
  );

  // Get statistics
  const stats = useMemo(
    () => getMappingStats(mappingData || null),
    [mappingData]
  );

  // Check if ready for code generation
  const canGenerateCode = useMemo(
    () => allMappingsAccepted(mappingData || null),
    [mappingData]
  );

  // Handle generate code
  const handleGenerateCode = async () => {
    try {
      await mappingVisualizationAPI.generateIngestionCode(caseId);
      alert('Code generation started! Check the code generation tab for progress.');
    } catch (err) {
      console.error('Failed to generate code:', err);
      alert('Failed to start code generation. Please try again.');
    }
  };

  // Handle network ready
  const handleNetworkReady = (net: Network) => {
    console.log('[MappingVisualization] Network received:', net);
    setNetwork(net);
  };

  // Zoom controls
  const handleZoomIn = () => {
    console.log('[MappingVisualization] Zoom in clicked, sankeyZoomAPI:', sankeyZoomAPI);
    if (sankeyZoomAPI) {
      sankeyZoomAPI.zoomIn();
    }
  };

  const handleZoomOut = () => {
    if (sankeyZoomAPI) {
      sankeyZoomAPI.zoomOut();
    }
  };

  const handleFitToScreen = () => {
    if (sankeyZoomAPI) {
      sankeyZoomAPI.resetZoom();
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Spinner size="lg" />
          <p className="mt-4 text-gray-600">Loading mapping visualization...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <Card className="p-8">
        <div className="flex items-start space-x-3 text-red-600">
          <AlertCircle className="w-6 h-6 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold mb-2">Failed to load mapping data</h3>
            <p className="text-sm text-gray-600 mb-4">
              {error instanceof Error ? error.message : 'An unknown error occurred'}
            </p>
            <Button onClick={() => refetch()} size="sm">
              Try Again
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  // Empty state
  if (!mappingData || mappingData.dataFiles.length === 0) {
    return (
      <Card className="p-8">
        <div className="text-center text-gray-600">
          <FileText className="w-12 h-12 mx-auto mb-4 text-gray-400" />
          <h3 className="text-lg font-semibold mb-2">No Data Files Found</h3>
          <p className="text-sm">
            Upload data files in the "Data Files" tab to see mapping visualization.
          </p>
        </div>
      </Card>
    );
  }

  const containerClasses = isFullScreen
    ? 'fixed inset-0 z-50 bg-white flex flex-col'
    : 'flex flex-col h-full';

  return (
    <div className={`${containerClasses} space-y-4 ${isFullScreen ? 'p-4' : ''}`}>
      {/* Toolbar */}
      <div className="flex items-center justify-between bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex items-center space-x-6">
          {/* Back Button */}
          {onBack && (
            <Button
              onClick={onBack}
              variant="outline"
              size="sm"
              className="mr-2"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          )}
          {/* Statistics */}
          <div className="flex items-center space-x-4 text-sm">
            <div>
              <span className="text-gray-600">Files:</span>
              <span className="ml-1 font-semibold">{stats.totalFiles}</span>
            </div>
            <div>
              <span className="text-gray-600">Columns:</span>
              <span className="ml-1 font-semibold">{stats.totalColumns}</span>
            </div>
            <div>
              <span className="text-gray-600">Mapped:</span>
              <span className="ml-1 font-semibold text-green-600">
                {stats.mappedColumns}
              </span>
            </div>
            <div>
              <span className="text-gray-600">Unmapped:</span>
              <span className="ml-1 font-semibold text-amber-600">
                {stats.unmappedColumns}
              </span>
            </div>
            {stats.detectedRelationships > 0 && (
              <div>
                <span className="text-gray-600">Relationships:</span>
                <span className="ml-1 font-semibold text-purple-600">
                  {stats.detectedRelationships}
                </span>
              </div>
            )}
          </div>

          {/* Column Filter Buttons */}
          <div className="flex items-center space-x-1 border-l pl-4">
            <Filter className="w-4 h-4 text-gray-500 mr-2" />
            <button
              onClick={() => setColumnFilterMode('all')}
              className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                columnFilterMode === 'all'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All Columns
            </button>
            <button
              onClick={() => setColumnFilterMode('mapped')}
              className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                columnFilterMode === 'mapped'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Mapped Only
            </button>
            <button
              onClick={() => setColumnFilterMode('unmapped')}
              className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                columnFilterMode === 'unmapped'
                  ? 'bg-amber-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Unmapped Only
            </button>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center space-x-3">
          {/* Show Relationships Toggle */}
          <label className="flex items-center space-x-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={filterOptions.showRelationships}
              onChange={(e) =>
                setFilterOptions((prev) => ({
                  ...prev,
                  showRelationships: e.target.checked,
                }))
              }
              className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
            />
            <GitBranch className="w-4 h-4 text-purple-600" />
            <span>Relationships</span>
          </label>

          {/* Zoom Controls */}
          <div className="flex items-center space-x-1 border-l pl-3">
            <Button
              onClick={handleZoomOut}
              variant="outline"
              size="sm"
              title="Zoom out"
              disabled={!sankeyZoomAPI}
            >
              <ZoomOut className="w-4 h-4" />
            </Button>
            <Button
              onClick={handleFitToScreen}
              variant="outline"
              size="sm"
              title="Reset zoom"
              disabled={!sankeyZoomAPI}
            >
              <Maximize2 className="w-3 h-3" />
            </Button>
            <Button
              onClick={handleZoomIn}
              variant="outline"
              size="sm"
              title="Zoom in"
              disabled={!sankeyZoomAPI}
            >
              <ZoomIn className="w-4 h-4" />
            </Button>
          </div>

          {/* Full Screen Toggle */}
          <Button
            onClick={() => setIsFullScreen(!isFullScreen)}
            variant="outline"
            size="sm"
            title={isFullScreen ? 'Exit fullscreen' : 'Enter fullscreen'}
          >
            {isFullScreen ? (
              <Minimize2 className="w-4 h-4" />
            ) : (
              <Maximize2 className="w-4 h-4" />
            )}
          </Button>

          {/* Generate Code Button */}
          <Button
            onClick={handleGenerateCode}
            variant="primary"
            size="sm"
            disabled={!canGenerateCode}
            title={
              canGenerateCode
                ? 'Generate data ingestion code'
                : 'Accept all mappings before generating code'
            }
          >
            <Code className="w-4 h-4 mr-2" />
            Generate Code
          </Button>
        </div>
      </div>

      {/* Visualization Canvas */}
      <Card className="flex-1 p-0 overflow-hidden">
        {sankeyData.nodes.length > 0 ? (
          <SankeyCanvas
            data={sankeyData}
            mappingData={mappingData || null}
            onNodeClick={setSelectedNode}
            onScaleReady={setSankeyZoomAPI}
          />
        ) : (
          <div className="flex items-center justify-center h-full text-gray-500">
            <div className="text-center">
              <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No mappings to display</p>
              <p className="text-sm mt-1">
                Adjust filters or analyze files to see mappings
              </p>
            </div>
          </div>
        )}
      </Card>

      {/* Mapping Progress Indicator */}
      {stats.totalColumns > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">
              Mapping Progress
            </span>
            <span className="text-sm text-gray-600">
              {stats.acceptedColumns} / {stats.totalColumns} accepted
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-green-600 h-2 rounded-full transition-all duration-300"
              style={{
                width: `${(stats.acceptedColumns / stats.totalColumns) * 100}%`,
              }}
            />
          </div>
          {canGenerateCode && (
            <div className="mt-2 flex items-center text-sm text-green-600">
              <Check className="w-4 h-4 mr-1" />
              All mappings accepted! Ready to generate code.
            </div>
          )}
        </div>
      )}

      {/* Legend */}
      <Card className="p-4">
        <h3 className="text-sm font-semibold mb-3">Legend</h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-xs">
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-blue-500 rounded"></div>
            <span>Source File</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-green-500 rounded"></div>
            <span>Accepted Column</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-amber-500 rounded"></div>
            <span>Suggested Column</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-red-500 rounded"></div>
            <span>Unmapped Column</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-green-200 rounded"></div>
            <span>Property</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-green-700 rounded"></div>
            <span>Neo4j Schema Node</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-8 h-1 bg-green-500"></div>
            <span className="text-green-600">High Confidence (≥90%)</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-8 h-1 bg-amber-500"></div>
            <span className="text-amber-600">Medium Confidence (70-89%)</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-8 h-1 bg-red-500"></div>
            <span className="text-red-600">Low Confidence (&lt;70%)</span>
          </div>
        </div>
        <div className="mt-3 text-xs text-gray-600">
          <p><strong>Tip:</strong> Click and drag to pan, use mouse wheel to zoom, or click the zoom buttons above. Hover over nodes for details.</p>
        </div>
      </Card>
    </div>
  );
};

export default MappingVisualization;
